import {useContext} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import SlideInPane from 'patient-ping-remedy/packages/slide-in-pane';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster, useRosterStore} from '@/store/roster_store';
import {ConnectContactChannel, ConnectContactResponse, ConnectContactStatus} from '@/api/dto/connect';
import {useInfiniteScroll} from '@/hooks/useInfiniteScroll';
import AwsCommunicationsContact from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContact';
import Loading from '@common/Loading';
import {Header, Contacts, GroupContext} from '@/components/pages/tasks/aws_communications/AwsCommunicationsDrawer.styles';

type Props = {
  isOpen: boolean;
  onClose(): void;
}

// React key for a contact card. SMS cards collapse to one card per conversation (keyed on the
// patient phone number), and the representative contact changes when a CTR sync adds a newer
// message. Keying on the volatile contact id would unmount/remount the card on each sync -- which
// tears down an open conversation modal. Key SMS cards on the stable conversation (phone number)
// so a representative swap re-renders the same instance with new props instead. See CARECO-5791.
export function contactCardKey(connectContact: ConnectContactResponse): string {
  return connectContact.channel === ConnectContactChannel.SMS
    ? `sms:${connectContact.phoneNumber ?? connectContact.id}`
    : `${connectContact.id}`;
}

export default function AwsCommunicationsDrawer({ isOpen, onClose }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const activeRoster = useActiveRoster();
  const selectedRoster = useRosterStore((state) => state.selectedRoster);

  // The Tasks page is cross-roster and has no group selector, so `useActiveRoster` is null there
  // (CARECO-5862 stopped the Tasks page from incidentally populating `currentRoster`). We resolve
  // the panel to the group the user last selected on the Clients page (`selectedRoster`), which
  // persists across navigation. We deliberately do NOT guess a fallback group: if nothing is
  // selected, the panel shows an explicit empty state rather than silently scoping to an arbitrary
  // roster. The true all-groups view is tracked in CARECO-5702.
  const resolvedRoster = activeRoster ?? selectedRoster;
  const rosterId = resolvedRoster?.id;

  const { data: connectContactsResponse, isLoading, isFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: ['connect', 'contacts', rosterId],
    queryFn: async ({ pageParam }) => carecoApi?.getConnectContacts(rosterId!, pageParam, 25),
    enabled: !!carecoApi && !!rosterId,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
    refetchInterval: 30_000,
  });

  const { ref, listElement } = useInfiniteScroll<HTMLDivElement>({
    enabled: !isLoading,
    fetching: isFetching,
    selector: '[data-communications-item]',
    onNextPage() {
      if (connectContactsResponse?.pages.at(-1)?.data.connectContacts.length) {
        void fetchNextPage();
      }
    },
  });

  const connectContacts = connectContactsResponse?.pages
    .flatMap((page) => page?.data.connectContacts ?? []) ?? [];

  // Collapse SMS contacts to one card per conversation. Each inbound/outbound SMS is its own
  // Connect contact, which would flood the panel with a card per message. Keep only the most
  // recent contact per patient number; "View conversation" on the card shows the full history.
  // Non-SMS contacts (calls, voicemail, email) are left one-card-each as before.
  //
  // Note: the server stitches a thread by (patient number, our Connect number), but the panel
  // DTO only exposes the patient number, so we collapse on that alone. In practice a roster uses
  // a single Connect SMS number, so this matches; if a patient ever converses across multiple
  // Connect numbers we'd add connectPhoneNumber to ConnectContactResponse and key on both.
  const latestSmsByConversation = new Map<string, typeof connectContacts[number]>();
  for (const contact of connectContacts) {
    if (contact.channel !== ConnectContactChannel.SMS) {
      continue;
    }
    const key = contact.phoneNumber ?? '';
    const existing = latestSmsByConversation.get(key);
    if (!existing
      || new Date(contact.contactedAt).getTime() > new Date(existing.contactedAt).getTime()) {
      latestSmsByConversation.set(key, contact);
    }
  }
  const dedupedContacts = connectContacts.filter((contact) =>
    contact.channel !== ConnectContactChannel.SMS
    || latestSmsByConversation.get(contact.phoneNumber ?? '') === contact);

  const connectContactsCompleted = dedupedContacts
    ?.filter((connectContact) => connectContact.status === ConnectContactStatus.COMPLETED);
  const connectContactsCountCompleted = connectContactsCompleted.length;

  const connectContactsPending = dedupedContacts
    ?.filter((connectContact) => connectContact.status === ConnectContactStatus.PENDING);
  const connectContactsCountPending = connectContactsPending.length;

  return (
    <SlideInPane
      isOpen={isOpen}
      onClose={onClose}
      headerText="AWS Communications"
      width="70rem"
      showNav={false}
    >
      <Banner type={BannerTypes.INFORMATIONAL}>
        Synced via AWS connect. Manage your missed communications from different channels.
      </Banner>
      {resolvedRoster ? (
        <GroupContext>
          Showing communications for: <strong>{resolvedRoster.name}</strong>
        </GroupContext>
      ) : (
        <Banner type={BannerTypes.WARNING}>
          No group selected. Select a group on the Clients page to view its communications.
        </Banner>
      )}
      <div ref={listElement}>
        {!!connectContactsPending?.length && (
          <>
            <Header>Pending ({connectContactsCountPending})</Header>
            <Contacts>
              {connectContactsPending.map((connectContact) => (
                <AwsCommunicationsContact
                  key={contactCardKey(connectContact)}
                  connectContact={connectContact}
                  rosterId={rosterId}
                  interactable
                />
              ))}
            </Contacts>
          </>
        )}
        {!!connectContactsCompleted?.length && (
          <>
            <Header>Completed ({connectContactsCountCompleted})</Header>
            <Contacts>
              {connectContactsCompleted.map((connectContact) => (
                <AwsCommunicationsContact
                  key={contactCardKey(connectContact)}
                  connectContact={connectContact}
                  rosterId={rosterId}
                  interactable
                />
              ))}
            </Contacts>
          </>
        )}
      </div>
      <span ref={ref} />
      {isFetching && <Loading />}
    </SlideInPane>
  );
}
